/**
 * Blok Vluchtrecorder in Instellingen > Meer. L5 van blok L (Frank, 19 oktober 2026).
 *
 * Alle sporen van een periode in de actieve werkruimte als JSON, met de ketencontrole
 * samengevat. De datums zijn LOKALE dagen; ze gaan als twee tijdstippen naar de server
 * (begin van de eerste dag tot begin van de dag na de laatste), zodat de server nooit
 * raadt welke dag een kale datum bedoelt. De dag erbij gaat met `setDate`, niet met 24
 * uur: op de dag van de wintertijd duurt een dag 25 uur.
 */
import { useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { API } from "@/lib/api";
import { useTaal } from "@/contexts/TaalContext";
import { dagVan, periodeVan } from "@/lib/periode";

// De twee pure functies wonen sinds 21 oktober 2026 in `lib/periode.js` (los punt
// wintertijdtoets); ze blijven hier ook te importeren.
export { dagVan, periodeVan };

const OORDEEL = {
  geverifieerd: "Keten geverifieerd",
  gebroken: "Keten gebroken",
  "deels zonder keten": "Deels zonder keten",
  "geen regels": "Geen sporen in deze periode",
};

export default function VluchtrecorderBlok() {
  const { t } = useTaal();
  const vandaag = new Date();
  const weekTerug = new Date(vandaag);
  weekTerug.setDate(weekTerug.getDate() - 6);
  const [van, setVan] = useState(dagVan(weekTerug));
  const [tot, setTot] = useState(dagVan(vandaag));
  const [bezig, setBezig] = useState(false);
  const [samen, setSamen] = useState(null);
  const [fout, setFout] = useState("");
  const periode = periodeVan(van, tot);

  const exporteer = async () => {
    if (!periode) return;
    setBezig(true);
    setFout("");
    setSamen(null);
    try {
      const r = await axios.get(`${API}/settings/vluchtrecorder`, { params: periode, responseType: "text" });
      const tekst = typeof r.data === "string" ? r.data : JSON.stringify(r.data);
      setSamen(JSON.parse(tekst).ketencontrole || null);
      const url = URL.createObjectURL(new Blob([tekst], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `novamund-vluchtrecorder-${van}-${tot}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setFout(t("De vluchtrecorder exporteren is mislukt."));
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="vluchtrecorder-blok">
      <h4 className="font-medium text-sm">{t("Vluchtrecorder")}</h4>
      <p className="text-xs text-muted-foreground">
        {t("Alle sporen van deze werkruimte in een periode, als JSON, met de ketencontrole samengevat. Beide dagen tellen mee.")}
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs space-y-1">
          <span className="block">{t("Van")}</span>
          <Input type="date" value={van} onChange={(e) => setVan(e.target.value)} className="h-11 md:h-8"
                 data-testid="vluchtrecorder-van" />
        </label>
        <label className="text-xs space-y-1">
          <span className="block">{t("Tot en met")}</span>
          <Input type="date" value={tot} onChange={(e) => setTot(e.target.value)} className="h-11 md:h-8"
                 data-testid="vluchtrecorder-tot" />
        </label>
        <Button type="button" variant="outline" size="sm" className="h-11 md:h-8" onClick={exporteer}
                disabled={bezig || !periode} data-testid="vluchtrecorder-exporteer">
          {bezig ? t("Bezig met exporteren...") : t("Exporteer vluchtrecorder")}
        </Button>
      </div>
      {!periode && <p className="text-xs text-destructive" data-testid="vluchtrecorder-ongeldig">{t("Kies een begin dat niet na het einde ligt.")}</p>}
      {samen && (
        <div className="text-xs space-y-0.5" data-testid="vluchtrecorder-samenvatting" data-oordeel={samen.oordeel}>
          <p className="font-medium">{t(OORDEEL[samen.oordeel] || "Keten gebroken")}</p>
          <p>{samen.regels} {t("sporen in")} {samen.gesprekken} {t("gesprekken")}: {samen.geverifieerd} {t("geverifieerd")}, {samen.gebroken} {t("gebroken")}, {samen.zonder_keten} {t("zonder keten")}.</p>
        </div>
      )}
      {fout && <p className="text-sm text-destructive" role="alert">{fout}</p>}
    </div>
  );
}
